"use client"

import type { components } from "umbraco"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { replaceRichTextLinks } from "utils"
import SectionTitleDecoration from "../../icons/SectionTitleDecoration"
import Container from "../../Container"
import {
  Root,
  TitleRow,
  SectionTitle,
  ChevronWrapper,
  ContentWrapper,
  DesktopPathWrapper,
  MobilePathWrapper,
  CardsRow,
  Card,
  CardTitle,
  CardBody,
} from "./styles"

// Position the arrow BEFORE the browser paints so it never flashes at the SVG
// origin (0,0 = top-left) on first render. On RTL that origin happens to match
// the correct start corner, but on English/LTR (start = top-right) the flash
// showed the arrow briefly on the wrong side — visible on slow connections
// (EGDE-289). useLayoutEffect runs only on the client; fall back to useEffect on
// the server to avoid the SSR warning (the SVG never renders server-side anyway).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect

type ValuePropositionsData =
  components["schemas"]["ValuePropositionsBlockPropertiesModel"]
type ValuePropositionItemData =
  components["schemas"]["ValuePropositionItemPropertiesModel"]

interface Props {
  data: ValuePropositionsData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Connector path generators
//
// The decorative dashed connector is generated from the live card count so the
// weave always spans exactly N cards. Hardcoded 3-/4-card paths used to break
// (or vanish) the moment an editor added another card (EGDE-121).
// ---------------------------------------------------------------------------

// Desktop weave geometry (matches Figma 3-card path: width 1205, top y=0.5,
// bottom y=298.5, corner radius 80). The path enters at the right (x=DW) on the
// top lane and weaves left to x=0, alternating lanes once per card boundary.
const DW = 1205
const D_TOP = 0.5
const D_BOT = 298.5
const D_R = 80
const D_K = D_R * 0.5523 // cubic-bezier circle approximation

/**
 * Horizontal weaving path across `count` cards, built in reading order. Each
 * card boundary owns one lane transition built from two quarter-circle corners
 * (radius D_R) plus a vertical straight between them — matching the Figma
 * 3-card geometry exactly, just repeated for any number of cards.
 *
 * EGDE-289/291: the path STARTS next to the section title (top, reading-start
 * edge) and runs toward the last card, so the animated arrow and the orange
 * trail flow with the reading direction. RTL starts at the right (unchanged);
 * LTR is the mirror, starting at the left.
 */
function buildDesktopPath(count: number, ltr: boolean): string {
  const endX = ltr ? DW : 0
  if (count < 2) return `M${ltr ? 0 : DW} ${D_TOP}H${endX}`
  const col = DW / count
  const step = ltr ? 1 : -1 // x walking direction from the start edge
  let y = D_TOP
  let d = `M${ltr ? 0 : DW} ${y}`
  for (let i = 1; i < count; i++) {
    const boundary = ltr ? col * i : DW - col * i // i-th boundary in walk order
    const xNear = boundary - step * D_R // where the lead-in corner starts
    const xFar = boundary + step * D_R // where the run-out corner ends
    const nextY = y === D_TOP ? D_BOT : D_TOP
    const dir = nextY > y ? 1 : -1
    const vStart = y + dir * D_R // vertical segment start
    const vEnd = nextY - dir * D_R // vertical segment end
    // Lead-in: horizontal -> vertical quarter corner at the boundary's near side.
    d += `H${xNear.toFixed(3)}`
    d += `C${(xNear + step * D_K).toFixed(3)} ${y} ${boundary.toFixed(3)} ${(y + dir * D_K).toFixed(3)} ${boundary.toFixed(3)} ${vStart.toFixed(3)}`
    // Vertical straight across the lane gap.
    d += `V${vEnd.toFixed(3)}`
    // Run-out: vertical -> horizontal quarter corner at the boundary's far side.
    d += `C${boundary.toFixed(3)} ${(nextY - dir * D_K).toFixed(3)} ${(xFar - step * D_K).toFixed(3)} ${nextY} ${xFar.toFixed(3)} ${nextY}`
    y = nextY
  }
  d += `H${endX}`
  return d
}

const NAV_ARROW =
  "M12.857 13.591C12.1019 14.0749 11.1693 14.1308 10.3623 13.7405L1.45664 9.42984C0.568533 9.00007 0.0101175 8.11694 -0.000838954 7.12464C-0.0117954 6.13233 0.526984 5.23708 1.40569 4.78781L10.2137 0.281606C11.0119 -0.126762 11.9455 -0.0912042 12.7111 0.375929C13.4764 0.843373 13.9397 1.66045 13.9496 2.56196L14.047 11.3788C14.0566 12.2797 13.6119 13.1068 12.857 13.591Z"

function DashedPathDesktop({
  isRtl,
  count,
  active,
}: {
  isRtl: boolean
  count: number
  active: boolean
}) {
  const path = buildDesktopPath(count, !isRtl)
  const trailRef = useRef<SVGAnimateElement>(null)
  const motionRef = useRef<SVGAnimateMotionElement>(null)

  // EGDE-291: the connector runs ONCE when scrolled into view instead of
  // looping forever. Both animations stay dormant (begin="indefinite") until
  // `active`, then beginElement() fires them a single time and they freeze in
  // their finished state (full orange trail, arrow resting at the last card).
  useEffect(() => {
    if (!active) return
    trailRef.current?.beginElement()
    motionRef.current?.beginElement()
  }, [active])

  return (
    <DesktopPathWrapper>
      <svg
        viewBox="-10 -10 1225 319"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        overflow="visible"
      >
        <defs>
          <linearGradient
            id="navGradD"
            x1="0.636"
            y1="12.863"
            x2="17.794"
            y2="-2.82"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.25" stopColor="#F86F3E" />
            <stop offset="0.68" stopColor="#FFB36E" />
            <stop offset="1" stopColor="#E49E6E" />
          </linearGradient>
          {/* EGDE-284: reveal mask that grows along the path in sync with the
              arrow, turning the dashed trail behind it orange. */}
          <mask
            id="trailMaskD"
            maskUnits="userSpaceOnUse"
            x="-20"
            y="-20"
            width="1260"
            height="360"
          >
            <path
              d={path}
              stroke="#fff"
              strokeWidth="4"
              fill="none"
              pathLength="1"
              strokeDasharray="1 1"
              strokeDashoffset="1"
            >
              <animate
                ref={trailRef}
                attributeName="stroke-dashoffset"
                from="1"
                to="0"
                dur="10s"
                begin="indefinite"
                repeatCount="1"
                fill="freeze"
                calcMode="linear"
              />
            </path>
          </mask>
        </defs>
        {/* Gray dashed connector (the not-yet-reached portion). */}
        <path d={path} stroke="#9E9E9E" strokeWidth="1" strokeDasharray="8 8" />
        {/* Orange dashed trail, revealed behind the travelling arrow. */}
        <path
          d={path}
          stroke="#F86F3E"
          strokeWidth="1"
          strokeDasharray="8 8"
          mask="url(#trailMaskD)"
        />
        {/* EGDE-291: hidden until active so the dormant (un-started) arrow
            doesn't flash at the SVG origin before the section scrolls in. */}
        <g style={{ visibility: active ? "visible" : "hidden" }}>
          <animateMotion
            ref={motionRef}
            dur="10s"
            begin="indefinite"
            repeatCount="1"
            fill="freeze"
            // EGDE-289: the NAV_ARROW glyph natively points inline-start, so it
            // must be rotated to lead the travel direction in BOTH directions.
            // The path itself is already mirrored per locale (buildDesktopPath),
            // so the rotate mode is the same for RTL and LTR — "auto" left the
            // LTR arrow pointing backward against its travel.
            rotate="auto-reverse"
            path={path}
            keyPoints="0;1"
            keyTimes="0;1"
            calcMode="linear"
          />
          <path
            d={NAV_ARROW}
            fill="url(#navGradD)"
            transform="translate(-7,-7)"
          />
        </g>
      </svg>
    </DesktopPathWrapper>
  )
}

// ---------------------------------------------------------------------------
// Mobile connector — MEASURED.
//
// On mobile the cards stack vertically and alternate left/right alignment, and
// their heights depend on the CMS copy. A fixed-geometry SVG (the old approach)
// could not know the real card pitch, so `preserveAspectRatio` scaling left the
// dashed weave drifting off the cards for 3/4/5+ cards (EGDE-121). Instead we
// measure the live card boxes and draw the snake in real pixel coordinates
// (viewBox === container px, 1:1), re-measuring on resize. This is correct for
// any card count and any card height.
// ---------------------------------------------------------------------------

// EGDE-121: the mobile connector reproduces the exact Figma node
// `40013295-48197` ("Advantages - Scrolling"). That vector is a serpentine
// where EACH card gets a half-circle bulge out to the alternating edge (right,
// left, right, …), the bulges joined by short horizontal runs in the gaps, with
// a lead-in from the top inline-start corner (where the arrow rests) and a final
// run straight down the last edge. The shape is rebuilt here from the measured
// card boxes so it adapts to any card count/height (the ticket's core ask) while
// matching the design. The bulge radius is half the card's pitch, so every
// bulge reaches the edge exactly like the Figma path.
const M_EDGE_INSET = 0.5 // matches the Figma 0.5px stroke inset
const M_KAPPA = 0.5523 // cubic-bezier approximation of a quarter circle

interface MobileGeo {
  w: number
  h: number
  path: string
}

function measureMobilePath(
  container: HTMLElement,
  isRtl: boolean,
): MobileGeo | null {
  const cards = Array.from(container.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement,
  )
  if (cards.length < 2) return null

  const base = container.getBoundingClientRect()
  const w = base.width
  const h = base.height
  if (w === 0 || h === 0) return null

  const rows = cards.map((c) => {
    const r = c.getBoundingClientRect()
    return { top: r.top - base.top, bottom: r.bottom - base.top }
  })

  const n = rows.length
  const leftX = M_EDGE_INSET
  const rightX = w - M_EDGE_INSET
  const laneW = rightX - leftX
  const k = M_KAPPA

  const yTop = rows[0].top
  const yBot = rows[n - 1].bottom
  // Gap midpoints between consecutive cards — where one bulge hands off to the
  // next via a short horizontal run.
  const gaps: number[] = []
  for (let i = 0; i < n - 1; i++) {
    gaps.push((rows[i].bottom + rows[i + 1].top) / 2)
  }

  // Card 0 bulges to the reading-start edge (right in RTL, left in LTR); cards
  // then alternate. The arrow rests at the OPPOSITE top corner, exactly as the
  // Figma node does (RTL: arrow top-left, first bulge on the right).
  const bulgesRight = (i: number) => (isRtl ? i % 2 === 0 : i % 2 === 1)
  const aboveY = (i: number) => (i === 0 ? yTop : gaps[i - 1])
  const belowY = (i: number) => (i === n - 1 ? yBot : gaps[i])
  // Radius = half the card's pitch (clamped so the chord can't cross centre),
  // so every bulge reaches its edge — the Figma property.
  const radOf = (i: number) =>
    Math.min((belowY(i) - aboveY(i)) / 2, laneW / 2)
  const chordOf = (i: number) =>
    bulgesRight(i) ? rightX - radOf(i) : leftX + radOf(i)

  // Arrow corner + lead-in across the top to the first card's chord.
  const arrowX = bulgesRight(0) ? leftX : rightX
  let d = `M${arrowX.toFixed(2)} ${yTop.toFixed(2)}`
  d += `H${chordOf(0).toFixed(2)}`

  for (let i = 0; i < n; i++) {
    const a = aboveY(i)
    const b = belowY(i)
    const rad = radOf(i)
    const cx = chordOf(i)
    const cy = (a + b) / 2
    const edge = bulgesRight(i) ? rightX : leftX
    const dir = bulgesRight(i) ? 1 : -1

    // Straight chord run down to where the bulge starts (only when the card is
    // taller than the bulge — i.e. the radius was clamped).
    if (cy - rad > a + 0.5) d += `V${(cy - rad).toFixed(2)}`

    // Quarter-circle out to the edge (chord top -> edge mid).
    d += `C${(cx + dir * k * rad).toFixed(2)} ${(cy - rad).toFixed(2)} ${edge.toFixed(2)} ${(cy - k * rad).toFixed(2)} ${edge.toFixed(2)} ${cy.toFixed(2)}`

    if (i === n - 1) {
      // Last card: terminate by running straight down the edge to the bottom.
      d += `V${yBot.toFixed(2)}`
    } else {
      // Quarter-circle back in to the chord (edge mid -> chord bottom).
      d += `C${edge.toFixed(2)} ${(cy + k * rad).toFixed(2)} ${(cx + dir * k * rad).toFixed(2)} ${(cy + rad).toFixed(2)} ${cx.toFixed(2)} ${(cy + rad).toFixed(2)}`
      // Straight chord run down to the gap (when the radius was clamped).
      if (b > cy + rad + 0.5) d += `V${b.toFixed(2)}`
      // Horizontal run across the gap to the next card's chord.
      d += `H${chordOf(i + 1).toFixed(2)}`
    }
  }

  return { w, h, path: d }
}

function DashedPathMobile({
  cardsRef,
  count,
  isRtl,
}: {
  cardsRef: React.RefObject<HTMLDivElement | null>
  count: number
  isRtl: boolean
}) {
  const [geo, setGeo] = useState<MobileGeo | null>(null)
  const grayPathRef = useRef<SVGPathElement>(null)
  const maskPathRef = useRef<SVGPathElement>(null)
  const arrowRef = useRef<SVGGElement>(null)

  useEffect(() => {
    const el = cardsRef.current
    if (!el) return
    const update = () => setGeo(measureMobilePath(el, isRtl))
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    Array.from(el.children).forEach((c) => {
      if (c instanceof HTMLElement) ro.observe(c)
    })
    window.addEventListener("resize", update)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [cardsRef, count, isRtl])

  // EGDE-296: on mobile the arrow tracks the USER'S SCROLL instead of auto-
  // playing once over a timer. As the section scrolls through the viewport the
  // arrow walks the serpentine and the orange trail fills behind it; scrolling
  // back moves it back. The arrow rests at the path START (top corner) at
  // progress 0 — which is the reading-start edge per measureMobilePath: top-
  // RIGHT on English/LTR (EGDE-289: it must start on the right) and top-left on
  // RTL. Geometry is measured in 1:1 px (viewBox === container) so getPointAt-
  // Length coordinates map straight onto the SVG.
  useIsomorphicLayoutEffect(() => {
    const cards = cardsRef.current
    const gray = grayPathRef.current
    if (!geo || !cards || !gray) return
    const total = gray.getTotalLength()
    if (!total) return

    let raf = 0
    const apply = () => {
      raf = 0
      const rect = cards.getBoundingClientRect()
      const vh = window.innerHeight || document.documentElement.clientHeight
      // EGDE-341 (QA r5): progress tracks the VIEWPORT CENTER through the
      // section, not the viewport top. The old -rect.top mapping kept the
      // arrow parked until the section top scrolled past the top of the
      // screen — on /why-us that means "it only starts moving when the
      // section is almost out of the screen". Instead:
      //   p = 0  → section top reaches the viewport center (arrow at start,
      //            still not pre-advanced on load — QA r3),
      //   p = 1  → section bottom reaches the viewport bottom (arrow lands on
      //            the last card just as it becomes fully visible).
      // The travel window is the full remaining section height, so the glide
      // is also slower than the old half-viewport floor ("moves too fast").
      const denom = Math.max(rect.height - vh * 0.5, vh * 0.25)
      const p = Math.max(0, Math.min(1, (vh * 0.5 - rect.top) / denom))
      maskPathRef.current?.setAttribute("stroke-dashoffset", String(1 - p))
      const here = total * p
      const at = gray.getPointAtLength(here)
      // EGDE-343: sample the tangent from a point just BEHIND to a point just
      // AHEAD so the direction stays well-defined even at the very end. Sampling
      // only "ahead" collapsed to the same point at p=1 (atan2(0,0) → 0°), which
      // snapped the arrow sideways exactly when it reached the final edge.
      const aheadLen = Math.min(total, here + 1)
      const behindLen = Math.max(0, aheadLen - 2)
      const ahead = gray.getPointAtLength(aheadLen)
      const behind = gray.getPointAtLength(behindLen)
      // NAV_ARROW points inline-start (−x); +180° makes it lead the travel
      // direction on both RTL and LTR (replaces the old rotate="auto-reverse").
      const angle =
        (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI +
        180
      const arrow = arrowRef.current
      if (arrow) {
        arrow.setAttribute(
          "transform",
          `translate(${at.x} ${at.y}) rotate(${angle})`,
        )
        // EGDE-289: reveal the arrow only once it's been positioned, so it never
        // paints at the SVG origin (top-left) before the first measurement.
        arrow.style.visibility = "visible"
      }
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply)
    }
    apply()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [geo, cardsRef])

  if (!geo) return null

  return (
    <MobilePathWrapper>
      <svg
        viewBox={`0 0 ${geo.w} ${geo.h}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        overflow="visible"
      >
        <defs>
          <linearGradient
            id="navGradM"
            x1="0.636"
            y1="12.863"
            x2="17.794"
            y2="-2.82"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.25" stopColor="#F86F3E" />
            <stop offset="0.68" stopColor="#FFB36E" />
            <stop offset="1" stopColor="#E49E6E" />
          </linearGradient>
          {/* EGDE-284/296: reveal mask grows along the path in sync with scroll
              (stroke-dashoffset is driven from the scroll handler). */}
          <mask id="trailMaskM" maskContentUnits="userSpaceOnUse">
            <path
              ref={maskPathRef}
              d={geo.path}
              stroke="#fff"
              strokeWidth="6"
              fill="none"
              pathLength="1"
              strokeDasharray="1 1"
              strokeDashoffset="1"
              vectorEffect="non-scaling-stroke"
            />
          </mask>
        </defs>
        {/* Gray dashed connector (the not-yet-reached portion). Also the path
            measured by the scroll handler (getPointAtLength). */}
        <path
          ref={grayPathRef}
          d={geo.path}
          stroke="#9E9E9E"
          strokeWidth="1"
          strokeDasharray="8 8"
          vectorEffect="non-scaling-stroke"
        />
        {/* Orange dashed trail, revealed behind the travelling arrow. */}
        <path
          d={geo.path}
          stroke="#F86F3E"
          strokeWidth="1"
          strokeDasharray="8 8"
          vectorEffect="non-scaling-stroke"
          mask="url(#trailMaskM)"
        />
        {/* Arrow — positioned + rotated along the path from the scroll handler.
            Hidden until the layout effect places it, so it never flashes at the
            SVG origin (top-left) on first paint (EGDE-289, English/LTR). */}
        <g ref={arrowRef} style={{ visibility: "hidden" }}>
          <path
            d={NAV_ARROW}
            fill="url(#navGradM)"
            transform="translate(-7,-7)"
          />
        </g>
      </svg>
    </MobilePathWrapper>
  )
}

export default function ValuePropositions({ data, culture }: Props) {
  const items = data.items?.items ?? []
  const isRtl = culture === "he-IL" || culture === "ar"
  const cardsRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(false)

  // EGDE-291: trigger the connector animation a single time when the block
  // scrolls into view, then disconnect so it never loops.
  useEffect(() => {
    const el = contentRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <Root data-block-type="value-propositions">
      <Container paddingMobile>
        <TitleRow>
          <ChevronWrapper>
            {/* EGDE-289: the chevron is drawn for RTL (apex points inline-start);
                mirror it on LTR pages so it points toward the heading. */}
            <SectionTitleDecoration
              style={isRtl ? undefined : { transform: "scaleX(-1)" }}
            />
          </ChevronWrapper>
          <SectionTitle>{data.sectionTitle}</SectionTitle>
        </TitleRow>
        <ContentWrapper ref={contentRef}>
          {items.length >= 2 && (
            <DashedPathDesktop
              isRtl={isRtl}
              count={items.length}
              active={active}
            />
          )}
          {items.length >= 2 && (
            <DashedPathMobile
              cardsRef={cardsRef}
              count={items.length}
              isRtl={isRtl}
            />
          )}
          <CardsRow ref={cardsRef} $count={items.length}>
            {items.map((block) => {
              const props = block.content
                .properties as ValuePropositionItemData | undefined
              if (!props) return null
              // EGDE-607/608: every other rich-text renderer in the project runs
              // the CMS markup through replaceRichTextLinks; this block injected
              // it raw. Umbraco emits internal links as their Delivery route
              // (/pages/careers/), which 404s on the site — the helper strips the
              // /pages segment (and rewrites media URLs) the same way the Rich
              // Content Block does.
              const markup = replaceRichTextLinks(props.cardBody?.markup)
              return (
                <Card key={block.content.id}>
                  <CardTitle>{props.cardTitle}</CardTitle>
                  {markup && (
                    <CardBody
                      dangerouslySetInnerHTML={{
                        __html: markup,
                      }}
                    />
                  )}
                </Card>
              )
            })}
          </CardsRow>
        </ContentWrapper>
      </Container>
    </Root>
  )
}
